/**
 * Procedural sample renderers. Each returns raw channel data so the engine
 * can wrap it in an AudioBuffer once and reuse it for every playback.
 */
import { addBurst, addMode, Biquad, normalizePeak, normalizeRms, onePole, pick, range, Saw, smoothRandom, type Rng } from './dsp';
import type { ReverbProfile } from './mapping';

const len = (sr: number, seconds: number) => Math.max(1, Math.round(sr * seconds));

// ---------------------------------------------------------------- textures

/** Loopable granular crunch. Grains wrap around the loop end so the seam is invisible. */
export function gravelLoop(sr: number, seconds: number, density: number, rng: Rng): Float32Array {
  const n = len(sr, seconds), out = new Float32Array(n), grain = new Float32Array(len(sr, .03));
  const bp = new Biquad('bandpass', 2000, 1.4, sr);
  for (let g = 0, count = Math.round(density * seconds); g < count; g++) {
    grain.fill(0);
    const amp = .2 + .8 * rng() ** 2;
    if (rng() < .62) addBurst(grain, 0, amp, range(rng, .0006, .0026), rng, bp.set('bandpass', range(rng, 1100, 5200), range(rng, .8, 2.2), sr), .0002, sr);
    else {
      const f = range(rng, 1600, 6400);
      addMode(grain, 0, f, amp * .7, range(rng, .0015, .006), sr);
      addMode(grain, 0, f * range(rng, 1.4, 2.3), amp * .3, range(rng, .001, .003), sr);
      addBurst(grain, 0, amp * .35, .0008, rng, undefined, .0001, sr);
    }
    const start = Math.floor(rng() * n);
    for (let i = 0; i < grain.length; i++) out[(start + i) % n] += grain[i];
  }
  return normalizeRms(out, .16);
}
/** Light, high grit for hardpack: sparse fine particles. */
export function gritLoop(sr: number, seconds: number, rng: Rng): Float32Array {
  const n = len(sr, seconds), out = new Float32Array(n), grain = new Float32Array(len(sr, .01));
  const bp = new Biquad('bandpass', 4000, 1.5, sr);
  for (let g = 0, count = Math.round(70 * seconds); g < count; g++) {
    grain.fill(0);
    addBurst(grain, 0, .2 + .8 * rng() ** 3, range(rng, .0003, .0012), rng, bp.set('bandpass', range(rng, 2800, 8000), 1.6, sr), .0001, sr);
    const start = Math.floor(rng() * n);
    for (let i = 0; i < grain.length; i++) out[(start + i) % n] += grain[i];
  }
  return normalizeRms(out, .12);
}
/** Soft, damped thumps of a tyre in forest loam, with a squishy mid layer. */
export function loamLoop(sr: number, seconds: number, rng: Rng): Float32Array {
  const n = len(sr, seconds), out = new Float32Array(n), grain = new Float32Array(len(sr, .3));
  const lp = new Biquad('lowpass', 260, .7, sr), mid = new Biquad('bandpass', 700, 1.2, sr);
  for (let g = 0, count = Math.round(10 * seconds); g < count; g++) {
    grain.fill(0);
    const amp = .35 + .65 * rng();
    addMode(grain, 0, range(rng, 52, 115), amp, range(rng, .022, .05), sr, .006);
    addBurst(grain, 0, amp * 2.2, range(rng, .018, .04), rng, lp.set('lowpass', range(rng, 180, 360), .7, sr), .004, sr);
    if (rng() < .55) addBurst(grain, Math.floor(range(rng, 0, .02) * sr), amp * .45, range(rng, .01, .03), rng, mid.set('bandpass', range(rng, 450, 1100), 1.4, sr), .003, sr);
    const start = Math.floor(rng() * n);
    for (let i = 0; i < grain.length; i++) out[(start + i) % n] += grain[i];
  }
  return normalizeRms(out, .16);
}
/** Dense droplets flicked off a wet tyre. */
export function sprayLoop(sr: number, seconds: number, rng: Rng): Float32Array {
  const n = len(sr, seconds), out = new Float32Array(n), grain = new Float32Array(len(sr, .012));
  for (let g = 0, count = Math.round(320 * seconds); g < count; g++) {
    grain.fill(0);
    const amp = .15 + .85 * rng() ** 2.5;
    addMode(grain, 0, range(rng, 2400, 9500), amp, range(rng, .0005, .0022), sr);
    addBurst(grain, 0, amp * .3, .0004, rng, undefined, .0001, sr);
    const start = Math.floor(rng() * n);
    for (let i = 0; i < grain.length; i++) out[(start + i) % n] += grain[i];
  }
  return normalizeRms(out, .14);
}

/** Smooth control signal in [-1, 1] for gusts, swells and bumps. */
export function modulation(sr: number, seconds: number, pointsPerSecond: number, rng: Rng): Float32Array {
  return smoothRandom(len(sr, seconds), seconds * pointsPerSecond, rng);
}
/** Surf swell control in [-1, 1]: slow build, crash, long wash, at irregular intervals. */
export function surfSwell(sr: number, seconds: number, rng: Rng): Float32Array {
  const n = len(sr, seconds), out = new Float32Array(n).fill(0);
  let t = 0;
  while (t < seconds) {
    const rise = range(rng, 1.6, 3), wash = range(rng, 3, 5.5), height = range(rng, .55, 1);
    for (let k = 0; k < (rise + wash) * sr; k++) {
      const x = k / sr, env = x < rise ? (x / rise) ** 2.2 : Math.exp(-(x - rise) / (wash * .4));
      const i = (Math.floor(t * sr) + k) % n;
      out[i] = Math.max(out[i], env * height);
    }
    t += rise + range(rng, 2.5, 5.5);
  }
  for (let i = 0; i < n; i++) out[i] = out[i] * 2 - 1;
  return out;
}
/** Syllabic babble envelope (sum of many talkers), centred on zero. */
export function babble(sr: number, seconds: number, talkers: number, rng: Rng): Float32Array {
  const n = len(sr, seconds), out = new Float32Array(n);
  for (let v = 0; v < talkers; v++) {
    let t = rng() * .4;
    while (t < seconds) {
      const d = range(rng, .09, .28), a = range(rng, .4, 1);
      for (let k = 0; k < d * sr; k++) { const i = (Math.floor(t * sr) + k) % n; out[i] += a * Math.sin(Math.PI * k / (d * sr)) ** 2; }
      t += d + (rng() < .18 ? range(rng, .3, .9) : range(rng, .02, .1));
    }
  }
  let mean = 0; for (let i = 0; i < n; i++) mean += out[i]; mean /= n;
  for (let i = 0; i < n; i++) out[i] -= mean;
  return normalizePeak([out], 1)[0];
}

// ---------------------------------------------------------------- impacts

/** Bike frame and components hitting the ground: inharmonic tube modes, several bounces. */
export function metalClatter(sr: number, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, 1.2));
  const ratios = [1, 2.756, 5.404, 8.933, 13.345];
  let t = 0, amp = 1;
  const hits = 4 + Math.floor(rng() * 4);
  const hp = new Biquad('highpass', 2500, .7, sr);
  for (let h = 0; h < hits; h++) {
    const start = Math.floor(t * sr), f0 = range(rng, 340, 900);
    ratios.forEach((ratio, k) => addMode(out, start, f0 * ratio * range(rng, .985, 1.015), amp * range(rng, .5, 1) / (1 + k * .6), range(rng, .09, .32) / (1 + k * .7), sr));
    addMode(out, start, range(rng, 1800, 3400), amp * .35, range(rng, .02, .06), sr); // spoke/bolt rattle
    addBurst(out, start, amp * .8, .003, rng, hp, .0002, sr);
    t += range(rng, .045, .19) * (1 + h * .15); amp *= range(rng, .45, .8);
  }
  return normalizePeak([out], .9)[0];
}
/** Rider's body hitting the ground: low sweep, padded mid thwack, cloth noise. */
export function bodyThud(sr: number, rng: Rng): Float32Array {
  const n = len(sr, .7), out = new Float32Array(n);
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr, f = 38 + 85 * Math.exp(-t / .07);
    phase += 2 * Math.PI * f / sr;
    out[i] = Math.sin(phase) * (1 - Math.exp(-t / .002)) * Math.exp(-t / .12);
  }
  addBurst(out, 0, 2.6, .07, rng, new Biquad('lowpass', 230, .8, sr), .002, sr);
  addBurst(out, 0, .55, .02, rng, new Biquad('bandpass', 900, .8, sr), .001, sr);
  addBurst(out, len(sr, .012), .3, .09, rng, new Biquad('bandpass', 2600, .7, sr), .01, sr); // jacket scuff
  return normalizePeak([out], .95)[0];
}
/** Stones scattering after an impact: dense then thinning grains plus a short slide. */
export function gravelScatter(sr: number, seconds: number, rng: Rng): Float32Array {
  const n = len(sr, seconds), out = new Float32Array(n);
  const bp = new Biquad('bandpass', 2000, 1.2, sr);
  for (let t = 0; t < seconds;) {
    const rate = 420 * Math.exp(-t / .22) + 26 * Math.exp(-t / .7) + 2;
    const start = Math.floor(t * sr), env = Math.exp(-t / .55);
    if (rng() < .7) addBurst(out, start, env * range(rng, .15, 1), range(rng, .0008, .003), rng, bp.set('bandpass', range(rng, 900, 5000), 1.6, sr), .0002, sr);
    else { const f = range(rng, 700, 3200); addMode(out, start, f, env * range(rng, .2, .8), range(rng, .006, .025), sr); addMode(out, start, f * 2.3, env * .2, .004, sr); }
    t += Math.max(.0005, -Math.log(1 - rng() * .999) / rate);
  }
  addBurst(out, 0, .5, .25, rng, new Biquad('bandpass', 1300, .6, sr), .01, sr);
  return normalizePeak([out], .9)[0];
}
/** Sliding scrape (tarmac or a skidding tyre), with a rough texture. */
export function scrape(sr: number, seconds: number, rng: Rng, centre = 1800): Float32Array {
  const n = len(sr, seconds), out = new Float32Array(n);
  const bp = new Biquad('bandpass', centre, .8, sr), lo = new Biquad('bandpass', centre * .33, 1, sr);
  const rough = smoothRandom(n, seconds * 40, rng);
  for (let i = 0; i < n; i++) {
    const t = i / sr, env = (1 - Math.exp(-t / .012)) * Math.exp(-t / (seconds * .38));
    const w = rng() * 2 - 1;
    out[i] = (bp.process(w) * 1.4 + lo.process(w) * .9) * env * (.6 + .4 * rough[i]);
  }
  return normalizePeak([out], .85)[0];
}
/** A wheel spinning down after a crash: decelerating freehub clicks, spoke tings and rim rub. */
export function wheelSpinTail(sr: number, seconds: number, rng: Rng, engagement = 36): Float32Array {
  const n = len(sr, seconds), out = new Float32Array(n);
  const rev0 = range(rng, 3.5, 5.5), tau = range(rng, .9, 1.4);
  let revPhase = 0, clickPhase = 0;
  const rub = new Biquad('bandpass', 1500, 1.1, sr);
  for (let i = 0; i < n; i++) {
    const t = i / sr, rev = rev0 * Math.exp(-t / tau);
    const prevRev = revPhase;
    revPhase += rev / sr; clickPhase += rev * engagement / sr;
    if (clickPhase >= 1) {
      clickPhase -= 1;
      const a = .35 * (rev / rev0) ** .35 * range(rng, .65, 1);
      addMode(out, i, 3600, a, .0007, sr); addMode(out, i, 5400, a * .6, .0004, sr);
    }
    if (Math.floor(revPhase) !== Math.floor(prevRev) && rng() < .35) addMode(out, i, range(rng, 1800, 2700), .07 * rev / rev0, .06, sr);
    out[i] += rub.process(rng() * 2 - 1) * .09 * (rev / rev0) * (.5 + .5 * Math.sin(2 * Math.PI * revPhase)) * (1 - Math.exp(-t / .05));
  }
  return normalizePeak([out], .8)[0];
}
/** Suspension bottoming out and the frame loading on landing. */
export function suspensionThump(sr: number, rng: Rng): Float32Array {
  const n = len(sr, .5), out = new Float32Array(n);
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr; phase += 2 * Math.PI * (42 + 50 * Math.exp(-t / .05)) / sr;
    out[i] = Math.sin(phase) * (1 - Math.exp(-t / .0025)) * Math.exp(-t / .09);
  }
  addBurst(out, 0, 2, .05, rng, new Biquad('lowpass', 190, .8, sr), .002, sr);
  addMode(out, len(sr, .004), range(rng, 240, 300), .25, .03, sr);
  return normalizePeak([out], .95)[0];
}
/** Tyre casing slapping the ground. */
export function tyreSlap(sr: number, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, .22));
  addBurst(out, 0, 1, .02, rng, new Biquad('bandpass', range(rng, 1300, 2000), .9, sr), .0004, sr);
  addBurst(out, 0, 1.1, .03, rng, new Biquad('lowpass', 260, .7, sr), .001, sr);
  addMode(out, 0, range(rng, 120, 160), .3, .03, sr, .002);
  return normalizePeak([out], .9)[0];
}
/** Chain slapping the chainstay and derailleur rattle. */
export function chainSlap(sr: number, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, .4));
  for (let k = 0, t = 0; k < 6 + Math.floor(rng() * 4); k++, t += range(rng, .008, .03)) {
    addMode(out, len(sr, t), range(rng, 2200, 4800), range(rng, .3, 1) * Math.exp(-t / .06), range(rng, .004, .012), sr);
  }
  addMode(out, 0, range(rng, 620, 760), .35, .08, sr);
  return normalizePeak([out], .85)[0];
}
/** Take-off: the tyre unloads with a low pop and the bike lifts. */
export function takeoffPop(sr: number, rng: Rng): Float32Array {
  const n = len(sr, .32), out = new Float32Array(n);
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr; phase += 2 * Math.PI * (95 + 90 * (1 - Math.exp(-t / .03))) / sr;
    out[i] = Math.sin(phase) * (1 - Math.exp(-t / .002)) * Math.exp(-t / .045) * .8;
  }
  addBurst(out, 0, .7, .035, rng, new Biquad('bandpass', 700, .8, sr), .003, sr);
  addMode(out, len(sr, .03), range(rng, 2600, 3400), .12, .01, sr);
  return normalizePeak([out], .9)[0];
}
/** Dull knock: tyre and rim striking a rock. */
export function knock(sr: number, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, .6));
  [[185, 1, .09], [410, .6, .05], [695, .35, .03], [1130, .2, .02]].forEach(([f, a, tau]) => addMode(out, 0, f * range(rng, .95, 1.05), a, tau, sr, .001));
  addBurst(out, 0, 1.2, .015, rng, new Biquad('lowpass', 420, .7, sr), .0005, sr);
  [1900, 3100, 4400].forEach(f => addMode(out, len(sr, .002), f * range(rng, .97, 1.03), .12, .12, sr));
  return normalizePeak([out], .92)[0];
}
/** Air swish for a trick: noise through a sweeping band. */
export function swish(sr: number, rng: Rng): Float32Array {
  const n = len(sr, .55), out = new Float32Array(n), bp = new Biquad('bandpass', 500, 1.4, sr);
  for (let i = 0; i < n; i++) {
    const u = i / n;
    if (i % 32 === 0) bp.set('bandpass', 380 + 2400 * Math.sin(Math.PI * u) ** 1.5, 1.4, sr);
    out[i] = bp.process(rng() * 2 - 1) * Math.sin(Math.PI * u) ** 2;
  }
  return normalizePeak([out], .9)[0];
}

// ---------------------------------------------------------------- UI cues

/** Bright two-note chime with detuned shimmer (stereo). */
export function gateChime(sr: number, f1: number, f2: number, rng: Rng): [Float32Array, Float32Array] {
  const L = new Float32Array(len(sr, 1.5)), R = new Float32Array(L.length);
  const partials: [number, number, number][] = [[1, 1, .6], [2, .42, .36], [3.01, .18, .2], [4.2, .1, .1], [5.43, .05, .06]];
  [[f1, 0], [f2, .085]].forEach(([f, at], note) => {
    const start = len(sr, at), gain = note ? 1 : .8;
    for (const [ratio, a, tau] of partials) {
      addMode(L, start, f * ratio - 1.6, a * gain, tau, sr, .002);
      addMode(R, start, f * ratio + 1.6, a * gain, tau, sr, .002);
    }
    const hp = new Biquad('highpass', 7500, .7, sr);
    addBurst(L, start, .06, .018, rng, hp, .0005, sr);
    addBurst(R, start, .06, .018, rng, hp, .0005, sr);
  });
  return normalizePeak([L, R], .8) as [Float32Array, Float32Array];
}
/** Countdown tick: clean, slightly hollow tone. */
export function countdownTick(sr: number, freq: number): Float32Array {
  const out = new Float32Array(len(sr, .45));
  addMode(out, 0, freq, 1, .12, sr, .004); addMode(out, 0, freq * 2, .3, .07, sr, .004); addMode(out, 0, freq * 3, .1, .04, sr, .004);
  addMode(out, 0, freq * .5, .25, .1, sr, .006);
  return normalizePeak([out], .9)[0];
}
/** GO: a bright major chord swell with sparkle (stereo). */
export function goChord(sr: number, rng: Rng): [Float32Array, Float32Array] {
  const L = new Float32Array(len(sr, 1.9)), R = new Float32Array(L.length);
  [1046.5, 1318.5, 1568, 2093].forEach((f, k) => {
    for (const [ratio, a, tau] of [[1, 1, .7], [2, .35, .4], [3, .12, .2]] as const) {
      addMode(L, len(sr, k * .012), f * ratio - 2, a / (1 + k * .3), tau, sr, .003);
      addMode(R, len(sr, k * .012), f * ratio + 2, a / (1 + k * .3), tau, sr, .003);
    }
  });
  addMode(L, 0, 523.25, .5, .5, sr, .004); addMode(R, 0, 523.25, .5, .5, sr, .004);
  const hp = new Biquad('highpass', 6000, .7, sr);
  for (let k = 0; k < 14; k++) { const at = len(sr, k * .035); addBurst(k % 2 ? L : R, at, .05 * Math.exp(-k / 6), .012, rng, hp, .0005, sr); }
  return normalizePeak([L, R], .8) as [Float32Array, Float32Array];
}
/** Crowd cheer swell: roar, "woo" voices, whistles and applause (stereo). */
export function crowdCheer(sr: number, seconds: number, rng: Rng): [Float32Array, Float32Array] {
  const n = len(sr, seconds), L = new Float32Array(n), R = new Float32Array(n);
  const env = (t: number) => (1 - Math.exp(-t / .3)) * (t < 1.4 ? 1 : Math.exp(-(t - 1.4) / 1.1));
  const bands = [[520, 1.2, 1], [1200, 1.4, .75], [2600, 1.6, .38]] as const;
  for (const ch of [L, R]) {
    const filters = bands.map(([f, q]) => new Biquad('bandpass', f, q, sr));
    for (let i = 0; i < n; i++) { const w = rng() * 2 - 1; let y = 0; filters.forEach((flt, k) => { y += flt.process(w) * bands[k][2]; }); ch[i] += y * env(i / sr) * .9; }
  }
  const vowels = [[600, 1000], [750, 1200], [450, 850], [400, 1900]];
  for (let v = 0; v < 12; v++) {
    const start = range(rng, 0, .7), dur = range(rng, .7, 2.1), f0 = range(rng, 200, 430), [fa, fb] = pick(rng, vowels);
    const saw = new Saw(rng()), a = new Biquad('bandpass', fa, 3, sr), b = new Biquad('bandpass', fb, 4, sr);
    const pan = rng(), gain = range(rng, .25, .6);
    for (let k = 0; k < dur * sr; k++) {
      const i = len(sr, start) + k; if (i >= n) break;
      const u = k / (dur * sr), f = f0 * (1 + .22 * Math.min(1, u * 5) - .12 * u) * (1 + .012 * Math.sin(2 * Math.PI * 5.5 * k / sr));
      const x = saw.next(f, sr), y = (a.process(x) + b.process(x) * .6) * Math.sin(Math.PI * u) ** .7 * gain;
      L[i] += y * (1 - pan); R[i] += y * pan;
    }
  }
  for (let w = 0; w < 3; w++) {
    const start = range(rng, .3, 1.4), dur = range(rng, .35, .7), f0 = range(rng, 1900, 2600), pan = rng(), wolf = rng() < .5;
    let phase = 0;
    for (let k = 0; k < dur * sr; k++) {
      const i = len(sr, start) + k; if (i >= n) break;
      const u = k / (dur * sr), f = wolf ? f0 * (u < .45 ? 1 + .5 * u / .45 : 1.3 - .5 * (u - .45)) : f0 * (1 + .25 * u);
      phase += 2 * Math.PI * f / sr;
      const y = Math.sin(phase) * Math.sin(Math.PI * u) * .12; L[i] += y * (1 - pan); R[i] += y * pan;
    }
  }
  const clap = new Biquad('bandpass', 1600, 1.1, sr), grain = new Float32Array(len(sr, .03));
  for (let t = .05; t < seconds;) {
    grain.fill(0); addBurst(grain, 0, range(rng, .3, 1), range(rng, .003, .007), rng, clap.set('bandpass', range(rng, 1000, 2400), 1.2, sr), .0003, sr);
    const pan = rng(), start = len(sr, t), e = env(t) * .35;
    for (let k = 0; k < grain.length && start + k < n; k++) { L[start + k] += grain[k] * e * (1 - pan); R[start + k] += grain[k] * e * pan; }
    t += 1 / (12 + 110 * env(t));
  }
  return normalizePeak([L, R], .85) as [Float32Array, Float32Array];
}

// ---------------------------------------------------------------- wildlife and world

export type BirdSpecies = 'finch' | 'tit' | 'robin' | 'lark' | 'warbler';
/** FM chirp note with a frequency trajectory, vibrato and a fast attack. */
function chirp(out: Float32Array, sr: number, at: number, dur: number, f0: number, f1: number, amp: number, opts: { fm?: number; index?: number; vibRate?: number; vibDepth?: number; curve?: number } = {}) {
  const start = len(sr, at), count = len(sr, dur), { fm = 2, index = .4, vibRate = 0, vibDepth = 0, curve = 1 } = opts;
  let phase = 0, mod = 0;
  for (let k = 0; k < count; k++) {
    const i = start + k; if (i >= out.length) break;
    const u = k / count, f = f0 + (f1 - f0) * u ** curve + vibDepth * Math.sin(2 * Math.PI * vibRate * k / sr);
    phase += 2 * Math.PI * f / sr; mod += 2 * Math.PI * f * fm / sr;
    const env = Math.min(1, u * 14) * (1 - u) ** .8;
    out[i] += Math.sin(phase + index * Math.sin(mod)) * env * amp;
  }
}
export function birdCall(sr: number, species: BirdSpecies, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, species === 'lark' ? 3.6 : 2.2));
  const p = range(rng, .9, 1.12);
  if (species === 'finch') {
    let t = 0, gap = .11;
    const notes = 9 + Math.floor(rng() * 5);
    for (let k = 0; k < notes; k++) { const f = (5600 - k * 200) * p; chirp(out, sr, t, .045, f, f - 900 * p, .8, { index: .3 }); t += gap; gap *= .93; }
    chirp(out, sr, t + .03, .12, 2900 * p, 5200 * p, 1, { index: .5, curve: .6 });
    chirp(out, sr, t + .17, .09, 4600 * p, 3300 * p, .7, { index: .4 });
  } else if (species === 'tit') {
    for (let k = 0, t = 0; k < 3 + Math.floor(rng() * 2); k++, t += .3) { chirp(out, sr, t, .09, 4900 * p, 4700 * p, .9, { index: .2 }); chirp(out, sr, t + .13, .1, 3700 * p, 3400 * p, .8, { index: .25 }); }
  } else if (species === 'lark') {
    for (let t = 0; t < 3.3;) { const d = range(rng, .035, .09), f = range(rng, 2800, 6200) * p; chirp(out, sr, t, d, f, f * range(rng, .75, 1.3), range(rng, .4, 1), { index: .5, vibRate: range(rng, 30, 70), vibDepth: range(rng, 50, 250) }); t += d + range(rng, .004, .03); }
  } else {
    const notes = 6 + Math.floor(rng() * 7), warbler = species === 'warbler';
    for (let k = 0, t = 0; k < notes; k++) {
      const d = range(rng, .03, warbler ? .08 : .16), f = range(rng, warbler ? 3500 : 2400, warbler ? 7200 : 6800) * p;
      chirp(out, sr, t, d, f, f * range(rng, .7, 1.35), range(rng, .5, 1), { index: range(rng, .1, .7), vibRate: warbler ? 0 : range(rng, 25, 55), vibDepth: warbler ? 0 : range(rng, 60, 220) });
      t += d + range(rng, .02, warbler ? .04 : .12);
    }
  }
  return normalizePeak([out], .9)[0];
}
/** Gull: harsh glided "kyow" calls, sometimes a laughing series. */
export function gullCall(sr: number, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, 2.6));
  const laugh = rng() < .35, calls = laugh ? 5 + Math.floor(rng() * 4) : 2 + Math.floor(rng() * 3), base = range(rng, .85, 1.15);
  const formant = new Biquad('bandpass', 2300, 1, sr), amps = [1, .75, .55, .38, .22, .12];
  let t = 0;
  for (let c = 0; c < calls; c++) {
    const dur = laugh ? range(rng, .1, .14) : range(rng, .2, .36), start = len(sr, t), count = len(sr, dur);
    let phase = 0;
    for (let k = 0; k < count; k++) {
      const i = start + k; if (i >= out.length) break;
      const u = k / count, glide = laugh ? 1.25 - .2 * u : u < .3 ? .8 + 1.0 * u / .3 * .8 : 1.6 - .7 * (u - .3);
      const f = 780 * base * glide * (1 - c * .03);
      phase += 2 * Math.PI * f / sr;
      let y = 0; for (let h = 0; h < 6; h++) y += Math.sin(phase * (h + 1)) * amps[h];
      y *= (1 + .4 * Math.sin(2 * Math.PI * 46 * k / sr)) * Math.min(1, u * 12) * (1 - u) ** .6;
      y += (rng() * 2 - 1) * .12 * (1 - u);
      out[i] += (y * .45 + formant.process(y) * 1.4) * (1 - c * .06);
    }
    t += dur + (laugh ? range(rng, .04, .07) : range(rng, .08, .22));
  }
  return normalizePeak([out], .9)[0];
}
/** Distant raptor: a long descending raspy scream over the canyon. */
export function hawkCall(sr: number, rng: Rng): Float32Array {
  const n = len(sr, 1.5), out = new Float32Array(n), base = range(rng, .92, 1.08);
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const u = i / n, f = base * (u < .1 ? 2600 + 500 * u / .1 : 3100 - 1000 * (u - .1) / .9);
    phase += 2 * Math.PI * f / sr;
    const rasp = 1 + .5 * Math.sin(2 * Math.PI * 78 * i / sr);
    out[i] = (Math.sin(phase) + .45 * Math.sin(2 * phase) + .2 * Math.sin(3 * phase) + (rng() * 2 - 1) * .25) * rasp * Math.min(1, u * 9) * (1 - u) ** 1.2;
  }
  return normalizePeak([out], .9)[0];
}
/** Bee or fly passing by: Doppler-shifted buzz panned across (stereo). */
export function beeFlyby(sr: number, rng: Rng): [Float32Array, Float32Array] {
  const n = len(sr, 3), L = new Float32Array(n), R = new Float32Array(n), saw = new Saw(), lp = new Biquad('lowpass', 1500, 1, sr);
  const f0 = range(rng, 190, 250), dir = rng() < .5 ? 1 : -1, tc = range(rng, 1.2, 1.8);
  for (let i = 0; i < n; i++) {
    const t = i / sr, f = f0 * (1 + .06 * Math.tanh((tc - t) / .35)) * (1 + .01 * Math.sin(2 * Math.PI * 9 * t));
    const amp = 1 / (1 + ((t - tc) / .55) ** 2) * Math.min(1, t * 3) * Math.min(1, (3 - t) * 3);
    const y = lp.process(saw.next(f, sr)) * amp, pan = .5 + .45 * dir * Math.tanh((t - tc) / .6);
    L[i] = y * (1 - pan); R[i] = y * pan;
  }
  return normalizePeak([L, R], .8) as [Float32Array, Float32Array];
}
/** Water slapping a hull or quay: lowpassed splash with a glug. */
export function waterSlap(sr: number, rng: Rng): Float32Array {
  const n = len(sr, .7), out = new Float32Array(n);
  addBurst(out, 0, 1.6, range(rng, .1, .18), rng, new Biquad('lowpass', range(rng, 450, 800), .8, sr), .018, sr);
  let phase = 0; const g0 = range(rng, 320, 480);
  for (let i = len(sr, .02); i < n; i++) { const t = i / sr - .02; phase += 2 * Math.PI * (g0 - 180 * Math.min(1, t / .06)) / sr; out[i] += Math.sin(phase) * .3 * Math.exp(-t / .05); }
  for (let k = 0; k < 4; k++) addMode(out, len(sr, range(rng, .03, .25)), range(rng, 1200, 3200), .08, .008, sr);
  return normalizePeak([out], .9)[0];
}
/** Halyard slapping an aluminium mast (fixed mast pitch per buffer). */
export function mastClink(sr: number, f0: number, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, 1.4));
  [[1, 1, .5], [2.73, .5, .26], [5.2, .3, .12], [8.5, .15, .06]].forEach(([r, a, tau]) => addMode(out, 0, f0 * r * range(rng, .99, 1.01), a, tau, sr));
  addBurst(out, 0, .3, .002, rng, new Biquad('highpass', 3000, .7, sr), .0002, sr);
  return normalizePeak([out], .85)[0];
}
/** Distant alpine cowbell clank: inharmonic bell partials. */
export function cowbell(sr: number, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, 1.6)), f0 = range(rng, 560, 860);
  for (let k = 0, t = 0; k < 2 + Math.floor(rng() * 3); k++, t += range(rng, .18, .45)) {
    [[1, 1, .35], [1.47, .6, .25], [2.09, .45, .18], [2.56, .3, .12], [3.12, .2, .08]].forEach(([r, a, tau]) => addMode(out, len(sr, t), f0 * r, a * range(rng, .6, 1), tau, sr, .001));
  }
  return normalizePeak([out], .85)[0];
}
/** Formant "announcer" phrase through a PA horn: unintelligible by design. */
export function paPhrase(sr: number, rng: Rng): Float32Array {
  const seconds = range(rng, 2.2, 3.4), n = len(sr, seconds), out = new Float32Array(n), saw = new Saw();
  const vowels = [[800, 1200, 2500], [500, 1800, 2500], [320, 2200, 2900], [500, 900, 2400], [380, 850, 2300]];
  const f = vowels[0].map(v => new Biquad('bandpass', v, 6, sr)), hp = new Biquad('highpass', 420, .7, sr), cons = new Biquad('bandpass', 4200, 1.5, sr);
  let t = .05;
  const f0 = range(rng, 105, 140);
  while (t < seconds - .3) {
    const word = Math.floor(range(rng, 2, 5));
    for (let s = 0; s < word && t < seconds - .2; s++) {
      const d = range(rng, .11, .22), v = pick(rng, vowels), start = len(sr, t), count = len(sr, d);
      v.forEach((freq, k) => f[k].set('bandpass', freq, 6, sr));
      const plosive = rng() < .5;
      for (let k = 0; k < count && start + k < n; k++) {
        const u = k / count, pitch = f0 * (1.15 - .3 * (t / seconds)) * (1 + .08 * Math.sin(Math.PI * u));
        const x = saw.next(pitch, sr);
        let y = f[0].process(x) + f[1].process(x) * .7 + f[2].process(x) * .35;
        y *= Math.sin(Math.PI * u) ** .5;
        if (plosive && u < .12) y += cons.process(rng() * 2 - 1) * (1 - u / .12) * .8;
        out[start + k] += y;
      }
      t += d + range(rng, .01, .04);
    }
    t += range(rng, .12, .35);
  }
  for (let i = 0; i < n; i++) out[i] = Math.tanh(hp.process(out[i]) * 3);
  return normalizePeak([out], .9)[0];
}
/** One four-bar loop of distant festival house music (kick, clap, bass). */
export function festivalLoop(sr: number, rng: Rng): Float32Array {
  const beat = 60 / 124, n = len(sr, beat * 16), out = new Float32Array(n);
  const roots = [55, 55, 43.65, 49];
  for (let b = 0; b < 16; b++) {
    const start = len(sr, b * beat);
    let phase = 0;
    for (let k = 0; k < .35 * sr && start + k < n; k++) { const t = k / sr; phase += 2 * Math.PI * (48 + 110 * Math.exp(-t / .028)) / sr; out[start + k] += Math.sin(phase) * Math.exp(-t / .2); }
    if (b % 2 === 1) addBurst(out, start, .3, .05, rng, new Biquad('bandpass', 1400, .9, sr), .001, sr);
    const saw = new Saw(), lp = new Biquad('lowpass', 380, 2, sr), off = len(sr, (b + .5) * beat), f = roots[Math.floor(b / 4)] * (b % 4 === 3 ? 1.5 : 1);
    for (let k = 0; k < beat * .45 * sr && off + k < n; k++) out[off + k] += lp.process(saw.next(f * 2, sr)) * .55 * Math.exp(-k / sr / .12);
  }
  return normalizePeak([out], .9)[0];
}

// ---------------------------------------------------------------- reverb

/**
 * Stereo impulse response: discrete reflections plus a frequency-dependent
 * diffuse tail. Damping darkens the tail as it decays.
 */
export function impulseResponse(sr: number, profile: ReverbProfile, rng: Rng): [Float32Array, Float32Array] {
  const n = len(sr, profile.length), channels: [Float32Array, Float32Array] = [new Float32Array(n), new Float32Array(n)];
  channels.forEach((data, ch) => {
    let y = 0;
    const pre = len(sr, .008);
    for (let i = pre; i < n; i++) {
      const t = (i - pre) / sr;
      const decay = Math.exp(-6.91 * t / profile.rt60) * Math.min(1, t / .012);
      const sparse = profile.density >= 1 || rng() < profile.density * .5 + .02 ? 1 : 0;
      const x = (rng() * 2 - 1) * decay * sparse / Math.sqrt(profile.density * .5 + .05);
      const cutoff = 9500 * Math.exp(-t * profile.damping * 2.4) + 700;
      y += onePole(cutoff, sr) * (x - y);
      data[i] = y;
    }
    for (const [delay, gain] of profile.echoes) {
      const at = len(sr, delay * (ch ? 1.035 : .97) + .008);
      const width = 6 + Math.floor(delay * 90);
      for (let k = 0; k < width && at + k < n; k++) data[at + k] += gain * (rng() * 2 - 1) * Math.exp(-k / (width * .35)) * 1.6;
    }
  });
  return normalizePeak(channels, .9) as [Float32Array, Float32Array];
}

// ---------------------------------------------------------------- extras

/** A single larger stone flicked by the tyre. */
export function pebble(sr: number, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, .06)), f = range(rng, 1400, 4200);
  addMode(out, 0, f, 1, range(rng, .004, .012), sr); addMode(out, 0, f * range(rng, 1.6, 2.6), .45, .004, sr);
  addBurst(out, 0, .5, .0012, rng, new Biquad('bandpass', f * 1.3, 1.2, sr), .0002, sr);
  return normalizePeak([out], .9)[0];
}
/** Air pushed out of the rider's lungs on impact. */
export function exhale(sr: number, rng: Rng): Float32Array {
  const out = new Float32Array(len(sr, .4));
  addBurst(out, 0, 1, .09, rng, new Biquad('bandpass', 850, 1.3, sr), .012, sr);
  addBurst(out, 0, .6, .07, rng, new Biquad('bandpass', 1700, 1.6, sr), .01, sr);
  return normalizePeak([out], .8)[0];
}
/** Distant two-tone ship horn across the marina. */
export function foghorn(sr: number, rng: Rng): Float32Array {
  const n = len(sr, 3.2), out = new Float32Array(n), a = new Saw(rng()), b = new Saw(rng()), lp = new Biquad('lowpass', 420, .9, sr);
  for (let i = 0; i < n; i++) {
    const t = i / sr, env = Math.min(1, t / .35) * Math.min(1, Math.max(0, (2.7 - t) / .5));
    out[i] = lp.process(a.next(98, sr) + b.next(130.8, sr) * .8) * env;
  }
  return normalizePeak([out], .85)[0];
}
/** Short triumphant brass fanfare with a timpani hit and cymbal swell (stereo). */
export function fanfare(sr: number, rng: Rng): [Float32Array, Float32Array] {
  const n = len(sr, 2.9), L = new Float32Array(n), R = new Float32Array(n);
  const G4 = 392, C5 = 523.25, E5 = 659.25, G5 = 783.99, C6 = 1046.5;
  const notes: [number, number, number, number][] = [[G4, 0, .12, .5], [G4, .15, .12, .5], [G4, .3, .12, .5], [C5, .46, 1.9, .6], [E5, .46, 1.9, .5], [G5, .46, 1.9, .45], [C6, .5, 1.85, .32]];
  notes.forEach(([f, at, dur, amp], v) => {
    const s1 = new Saw(rng()), s2 = new Saw(rng()), lp = new Biquad('lowpass', 800, .9, sr), start = len(sr, at), count = len(sr, dur + .35), pan = .5 + (v % 3 - 1) * .22;
    for (let k = 0; k < count && start + k < n; k++) {
      const t = k / sr, rel = t > dur ? Math.exp(-(t - dur) / .09) : 1;
      const env = Math.min(1, t / .025) * (.82 + .18 * Math.exp(-t / .08)) * rel;
      if (k % 32 === 0) lp.set('lowpass', 700 + 3600 * env * (t < .06 ? t / .06 : 1) * (dur > 1 ? .75 + .25 * Math.exp(-t / .6) : 1), .9, sr);
      const vib = 1 + (t > .3 ? .004 * Math.sin(2 * Math.PI * 5.2 * t) : 0);
      const y = lp.process(s1.next(f * vib * 1.003, sr) + s2.next(f * vib * .997, sr)) * env * amp;
      L[start + k] += y * (1 - pan); R[start + k] += y * pan;
    }
  });
  let phase = 0; const at = len(sr, .46);
  for (let k = 0; k < sr * 1.2 && at + k < n; k++) { const t = k / sr; phase += 2 * Math.PI * (72 + 40 * Math.exp(-t / .05)) / sr; const y = Math.sin(phase) * Math.exp(-t / .35) * .7; L[at + k] += y; R[at + k] += y; }
  const hp = new Biquad('highpass', 5500, .7, sr), hp2 = new Biquad('highpass', 5500, .7, sr);
  for (let k = 0; k < sr * 2.2 && at + k < n; k++) { const t = k / sr, e = Math.min(1, t / .02) * Math.exp(-t / .6) * .22; L[at + k] += hp.process(rng() * 2 - 1) * e; R[at + k] += hp2.process(rng() * 2 - 1) * e; }
  return normalizePeak([L, R], .85) as [Float32Array, Float32Array];
}
